class BasicHeader extends HTMLElement {
  constructor() {
    super();
  }

  get headerSection() {
    return document.querySelector(".f-section-header");
  }

  connectedCallback() {
    this.classes = {
      active: "f-header__mega-active",
      headerScheme: this.dataset.headerColorScheme,
      dropdownScheme: this.dataset.dropdownColorScheme,
    };

    this.grandLinks = this.querySelectorAll(".f-site-nav__sub-item--has-child");
    this.grandLinks &&
      this.grandLinks.forEach((item) => {
        this.handleGrandLinksPosition(item);
      });
  }

  handleMegaItemActive(dropdown) {
    // Calculate backdrop height.
    const rect = dropdown.getBoundingClientRect();
    this.style.setProperty(
      "--f-dropdown-height",
      Math.ceil(rect.height) + "px"
    );

    this.classList.remove(this.classes.headerScheme);
    this.classList.add(this.classes.active, this.classes.dropdownScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleMegaItemDeactive() {
    this.classList.remove(this.classes.active, this.classes.dropdownScheme);
    this.classList.add(this.classes.headerScheme);
    document.documentElement.style.setProperty(
      "--f-header-height",
      this.clientHeight + "px"
    );
  }

  handleGrandLinksPosition(target) {
    const dropdownLV3 = target.querySelector(".f-site-nav__dropdown");
    if (dropdownLV3) {
      const rect = dropdownLV3.getBoundingClientRect();
      dropdownLV3.classList.remove("f-site-nav__dropdown-reversed");
      if (
        (!FoxThemeSettings.isRTL &&
          document.documentElement.clientWidth < rect.x + rect.width + 10) ||
        (FoxThemeSettings.isRTL && rect.x < 10)
      ) {
        dropdownLV3.classList.add("f-site-nav__dropdown-reversed");
      }
    }
  }
}
customElements.define("basic-header", BasicHeader, { extends: "header" });

class StickyHeader extends BasicHeader {
  constructor() {
    super();

    this.stickyClasses = {
      pinned: "header-pinned",
    };
  }

  connectedCallback() {
    super.connectedCallback();

    this.headerSection.classList.add("header-sticky");
    this.stickyType = this.dataset.stickyType;
    this.currentScrollTop = 0;

    this.headerBounds = this.headerSection
      .querySelector(".header")
      .getBoundingClientRect();

    this.onScrollHandler = this._onScroll.bind(this);
    this._onScroll();
    window.addEventListener("scroll", this.onScrollHandler, false);
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.onScrollHandler);
  }

  get isAlwaysSticky() {
    return this.stickyType === "always";
  }

  _onScroll() {
    const scrollTop = window.scrollY;
    const headerSection = this.headerSection;

    // Avoid layout thrashing by caching values
    const headerBoundsTop = this.offsetTop + this.headerBounds.height;

    const headerBoundsBottom =
      this.headerBounds.top + this.headerBounds.height + 100;

    // Utilize batch CSS classes and updates
    requestAnimationFrame(() => {
      if (scrollTop > headerBoundsTop) {
        headerSection.classList.add("header-scrolled");

        if (this.isAlwaysSticky) {
          document.body.classList.add(this.stickyClasses.pinned);
        } else {
          // Sticky on scroll up.
          if (
            scrollTop < this.currentScrollTop ||
            scrollTop < headerBoundsBottom
          ) {
            document.body.classList.add(this.stickyClasses.pinned);
          } else {
            document.body.classList.remove(this.stickyClasses.pinned);
          }
        }
      } else {
        headerSection.classList.remove("header-scrolled");
        document.body.classList.remove(this.stickyClasses.pinned);
      }

      this.currentScrollTop = scrollTop;
    });
  }

  _closeMenuDisclosure() {
    this.disclosures =
      this.disclosures || this.header.querySelectorAll("header-menu");
    this.disclosures.forEach((disclosure) => disclosure.close());

    this.siteNav = this.siteNav || this.header.querySelector("site-nav");
    this.siteNav && this.siteNav.closeMegaDropdowns();
  }
}
customElements.define("sticky-header", StickyHeader, { extends: "header" });

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };
    this.header.timeoutEnter = null;
    this.header.timeoutLeave = null;
  }

  onToggle(evt) {
    const { target } = evt;
    const li = target.closest(".f-site-nav__item");
    const isOpen = this.mainDetailsToggle.open;
    const isMega = li.classList.contains("f-site-nav__item--mega");

    if (!isOpen) {
      clearTimeout(this.header.timeoutEnter);
      li.classList.remove(this.classes.itemActive);
      this.header.timeoutLeave = setTimeout(() => {
        this.header.handleMegaItemDeactive();
      }, 160);
    } else {
      if (isMega) {
        clearTimeout(this.header.timeoutLeave);
        const dropdown = li.querySelector(".f-site-nav__dropdown");
        this.header.handleMegaItemActive(dropdown);
        this.header.timeoutEnter = setTimeout(() => {
          li.classList.add(this.classes.itemActive);
        }, 160);
      } else {
        li.classList.add(this.classes.itemActive);
      }
    }
  }
}
customElements.define("header-menu", HeaderMenu);

class SiteNav extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.header = this.closest(".site-header");
    this.classes = {
      itemActive: "f-menu__item-active",
    };

    this.megaItems = this.querySelectorAll(".f-site-nav__item--mega");
    this.timeoutEnter = null;
    this.timeoutLeave = null;
    this.isHover =
      this.header &&
      this.header.classList.contains("show-dropdown-menu-on-hover");
    if (this.isHover) {
      this.megaItems &&
        this.megaItems.forEach((megaItem, index) => {
          megaItem.addEventListener("mouseenter", (evt) =>
            this.onMenuItemEnter(evt, index)
          );
          megaItem.addEventListener("mouseleave", (evt) =>
            this.onMenuItemLeave(evt, index)
          );
        });
    }
  }

  onMenuItemEnter(evt) {
    clearTimeout(this.timeoutLeave);

    const { target } = evt;

    /**
     * If hover from prev item to sibling items then need to close prev sub menu immediately.
     */
    if (!target.classList.contains(this.classes.itemActive)) {
      this.megaItems &&
        this.megaItems.forEach((megaItem) => {
          megaItem.classList.remove(this.classes.itemActive);
        });
    }

    const dropdown = target.querySelector(".f-site-nav__dropdown");

    if (dropdown) {
      this.header.handleMegaItemActive(dropdown);

      this.timeoutEnter = setTimeout(() => {
        target.classList.add(this.classes.itemActive);
      });
    }
  }

  onMenuItemLeave(evt) {
    const { target } = evt;

    clearTimeout(this.timeoutEnter);
    
    this.header.handleMegaItemDeactive();

    this.timeoutLeave = setTimeout(() => {
      target.classList.remove(this.classes.itemActive);
    });
  }

  closeMegaDropdowns() {
    clearTimeout(this.timeoutEnter);
    this.megaItems &&
      this.megaItems.forEach((megaItem) => {
        megaItem.classList.remove(this.classes.itemActive);
      });
    this.timeoutLeave = setTimeout(() => {
      this.header.handleMegaItemDeactive();
    });
  }

  disconnectedCallback() {
    this.megaItems &&
      this.megaItems.forEach((megaItem, index) => {
        megaItem.removeEventListener("mouseenter", (evt) =>
          this.onMenuItemEnter(evt, index)
        );
        megaItem.removeEventListener("mouseleave", (evt) =>
          this.onMenuItemLeave(evt, index)
        );
      });
  }
}
customElements.define("site-nav", SiteNav);

class HeaderAccount extends HTMLElement {
  async connectedCallback() {
    const account = this.querySelector('shopify-account');

    if (!account) return;

    await customElements.whenDefined('shopify-account');

    this.injectAnimation(account);
    // this.watchDialog(account);
  }

  injectAnimation(account) {
    const shadow = account.shadowRoot;

    if (!shadow || shadow.querySelector('#custom-account-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-account-styles';

    style.textContent = `
      @keyframes dialogScaleIn {
        0% {
          transform: scale(0.8);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @keyframes dialogScaleOut {
        0% {
          transform: scale(1);
          opacity: 1;
        }

        100% {
          transform: scale(0.8);
          opacity: 0;
        }
      }
      @keyframes dialogBackdropIn {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }
      @keyframes dialogBackdropOut {
        0% {
          opacity: 1;
        }

        100% {
          opacity: 0;
        }
      }
      @media (min-width: 751px) {
        dialog[open] {
          animation: dialogScaleIn 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing {
          animation: dialogScaleOut 0.6s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog[open]::backdrop {
          cursor: zoom-out;
          animation: dialogBackdropIn 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
        dialog.closing::backdrop {
          animation: dialogBackdropOut 0.8s cubic-bezier(0.7, 0, 0.2, 1) !important;
        }
      }
    `;

    shadow.prepend(style);
  }

  watchDialog(account) {
    const shadow = account.shadowRoot;
    if (!shadow) return;

    const dialog = shadow.querySelector('dialog');
    if (!dialog) return;

    const update = () => {
      document.documentElement.classList.toggle('account-dialog-open', dialog.hasAttribute('open'));
    };

    update();

    new MutationObserver(update).observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }
}

customElements.define('header-account', HeaderAccount);

class MobileNav extends HTMLElement {
  connectedCallback() {
    // Give collapsible-tab elements time to upgrade before wiring listeners
    requestAnimationFrame(() => this._init());
  }

  _init() {
    // ── Requirements 1 & 2 ───────────────────────────────────────────────────
    // Level-1 items: clicking the <a> link opens the collapsible (Req 2), and
    // when the level-1 collapsible opens, every level-2 child with grandchildren
    // is auto-opened first (Req 1).
    //
    // Why we can't pre-open children before level-1.open():
    // - open() calls querySelectorAll('collapsible-tab') on the nearest
    //   [data-collapsible-parent] — unscoped, so it finds ALL descendant
    //   collapsible-tabs and closes any that are selected. Children we force-
    //   open first get immediately closed again (the visible flash).
    //
    // Solution:
    // 1. Temporarily set oneAtATime = false on level-1 before calling open(),
    //    so it skips the sibling-closing logic entirely.
    // 2. Call level-1.open() — expand() measures scrollHeight here against
    //    still-collapsed children (height will be too small), but that's
    //    corrected in step 4.
    // 3. Restore oneAtATime = true immediately.
    // 4. In the next rAF, force-open children instantly (no animation) then
    //    re-set content.style.height = scrollHeight so the level-1 animation
    //    target is corrected to the full expanded height.
    const level1Items = this.querySelectorAll(
      '.f-mobile-nav__item--has-child[data-menu-toggle-item]'
    );

    level1Items.forEach((li) => {
      const link        = li.querySelector(':scope > a.f-mobile-nav__link');
      const collapsible = li.querySelector(':scope > collapsible-tab');
      if (!link || !collapsible) return;

      const content = collapsible.querySelector('[data-content]');
      if (!content) return;

      const openLevel1 = () => {
        // Temporarily disable oneAtATime so open() doesn't close the children
        // we're about to force-open via _preOpenLevel2Children.
        // open() → querySelectorAll('collapsible-tab') is unscoped and would
        // find and close our level-2 children otherwise.
        collapsible.oneAtATime = false;
        collapsible.open();
        collapsible.oneAtATime = true;

        // Force-open children and correct the level-1 height in the next frame.
        // expand() already set content.style.height = scrollHeight px (measured
        // against collapsed children), so we re-measure after children are open.
        requestAnimationFrame(() => {
          this._preOpenLevel2Children(content);
          content.style.height = content.scrollHeight + 'px';
        });
      };

      // Req 2 – link click opens or closes the level-1 panel
      link.addEventListener('click', (e) => {
        e.preventDefault();
        if (collapsible.selected) {
          collapsible.close();
        } else {
          openLevel1();
        }
      });

      // Req 1 – intercept the arrow trigger in capture phase so our sequence
      // runs instead of collapsible-tab's own toggle()
      collapsible.querySelector('[data-trigger]')
        .addEventListener('click', (e) => {
          if (!collapsible.selected) {
            e.preventDefault();
            e.stopImmediatePropagation();
            openLevel1();
          }
          // If already selected, let collapsible-tab handle the close normally
        }, true);
    });

    // ── Requirement 3 ────────────────────────────────────────────────────────
    // Level-2 items: clicking the <a> sub-link also opens the level-3 dropdown.
    const level2Items = this.querySelectorAll(
      '.f-mobile-nav__subitem.f-mobile-nav__sub-item--has-child[data-menu-toggle-item]'
    );

    level2Items.forEach((li) => {
      const subLink     = li.querySelector(':scope > a.f-mobile-nav__sub-link');
      const collapsible = li.querySelector(':scope > collapsible-tab');
      if (!subLink || !collapsible) return;

      subLink.addEventListener('click', (e) => {
        e.preventDefault();
        collapsible.selected ? collapsible.close() : collapsible.open();
      });
    });
  }

  // Instantly sets a collapsible-tab to its fully-open end state without
  // triggering expand()'s animation or open()'s oneAtATime sibling-closing.
  // Mirrors exactly what expand() → transitionend → resetHeight() produces.
  _forceOpenInstant(collapsible) {
    if (!collapsible.content) return; // not yet initialised

    collapsible.isOpen   = true;
    collapsible.selected = true;

    collapsible.content.style.height     = 'auto';
    collapsible.content.style.transition = null;

    collapsible.classList.remove(collapsible.collapsedClass);
    collapsible.classList.add(collapsible.expandedClass, collapsible.selectedClass);
    collapsible.setAttribute('open', 'true');
    collapsible.setExpandedAria();
  }

  // Force-opens every level-2 child collapsible that has grandchildren and
  // isn't already open, without animation, before the level-1 panel opens.
  _preOpenLevel2Children(content) {
    content.querySelectorAll(
      '.f-mobile-nav__sub-item--has-child[data-menu-toggle-item]'
    ).forEach((li) => {
      const collapsible = li.querySelector(':scope > collapsible-tab');
      if (collapsible && !collapsible.selected) {
        this._forceOpenInstant(collapsible);
      }
    });
  }
}
customElements.define('mobile-nav', MobileNav);